/**
 * Exhaustive list of path prefixes that require that a user must
 * be authenticated and must belong to a Chalk "Team" in order to see them.
 * If an unauthed user or user who does not belong to a team goes to one of
 * these paths, they should be bounced to an appropriate page that allows them
 * to either auth or create/join a team.
 */
const DASHBOARD_REQUIRES_TEAM_MEMBERSHIP_PATH_PREFIXES = [
  "/projects",
  "/environments",
  "/settings",
  "/slack-redirect",
  "/github-redirect",
  "/cloud-accounts",
  "/cloud-overview",
  "/site-overview",
  "/migration-status",
  // Employee-only, but still a team-scoped dashboard page. Registered explicitly rather than
  // relying on the "/signup" prefix in the networking list below happening to cover it.
  "/signup-codes",
  "/signup-links",
  "/license-keys",
  "/cloud-vpcs",
  "/cloud-clusters",
  "/cloud-container-registries",
  "/cloud-storage",
  "/recovery-clusters",
  // Onboarding is split: create-team must stay reachable for authed users with no team yet
  // (unauthenticated networking wrapper), while create-environment requires a team and gets
  // the full authed wrapper (TeamProvider, permissions context, etc.).
  "/onboarding/create-environment",
];

/**
 * Check whether a given path requires authentication + team membership.
 */
export function pathRequiresAuthenticationAndTeamMembership(path: string): boolean {
  return DASHBOARD_REQUIRES_TEAM_MEMBERSHIP_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Exhaustive list of path prefixes that contain pages that require a
 * connect gRPC query client in order to make calls to the Chalk backend.
 * This is relevant because these pages must be server-side-rendered live
 * in response to requests in the special case of a customer-hosted frontend,
 * and this introduces additional load time that we don't want to have for our
 * marketing and non-networked pages.
 *
 * This is a superset of the paths that require auth and team membership
 *
 * Keep this list in sync with the static matcher in proxy.ts. Next statically analyzes
 * middleware matcher values at build time, so proxy.ts cannot generate its matcher from this
 * constant.
 */
const DASHBOARD_REQUIRES_NETWORKING_PATH_PREFIXES = [
  ...DASHBOARD_REQUIRES_TEAM_MEMBERSHIP_PATH_PREFIXES,
  // Notebook share links. Deliberately absent from the team-membership list
  // above: a public share link must render for a visitor with no Chalk account,
  // so this page authenticates with the token in its URL instead. Access is
  // enforced server-side by that token — this list only decides whether the
  // page gets a gRPC client, never what it is allowed to read.
  "/shared",
  "/onboarding",
  "/hack-harvard/welcome",
  // Invite links. Deliberately absent from the team-membership list above: the whole point
  // is that a visitor with no Chalk account can open one. It needs a gRPC client only
  // because it renders the shared LoginForm, which looks up SSO providers.
  "/join",
  "/signup",
  "/login",
  "/cli",
  "/test-components",
  "/local-plans",
];

/**
 * Check whether a given path requires a networking client (and therefore server-side rendering for self-hosted customers)
 */
export function pathRequiresNetworkingClient(path: string): boolean {
  return DASHBOARD_REQUIRES_NETWORKING_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}
